import { useCallback, useEffect, useState } from "react";
import { AirdropAllocationType, AirdropType } from "../../../type";
import {
  getAirdopDetail,
  getAirdropAllocation,
  updateTransaction,
} from "../../../api/apiAirdrop";
import { message, Skeleton, Tabs } from "antd";
import { useParams } from "react-router-dom";
import { GrShareOption } from "react-icons/gr";
import { RiGlobalLine, RiTwitterXLine } from "react-icons/ri";
import { FaDiscord, FaTelegramPlane } from "react-icons/fa";
import { formatTimeCommaDate } from "../../../helpers/formatter";
import AirdropInfo from "./AirdropInfo";
import AirdropReward from "./AirdropReward";
import { useAccount, useSwitchChain } from "wagmi";
import { useContract } from "../../../components/utils/useContract";
import airdropABI from "../abi/airdropABI.json";
import { useNetworkState } from "../../../components/utils/useNetwork";
import AirdropClaimCountdown from "./AirdropClaimCountdown";

const AirdropDetailSkeleton = () => {
  return (
    <>
      <Skeleton>
        <Skeleton.Avatar></Skeleton.Avatar>
        <Skeleton paragraph={{ rows: 2 }} />
      </Skeleton>
      <div className="border-2 border-solid border-customBlue-400"></div>
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="w-full lg:w-[62%] h-auto aspect-[calc(624/363)]">
          <div className="h-full w-full object-contain bg-center bg-customBlue-200" />
        </div>
        <div className="w-full lg:w-[38%] flex flex-col">
          <div className="flex-1 w-full min-h-40 bg-backgroundWhite p-4 text-text-secondary text-left"></div>
        </div>
      </div>
    </>
  );
};

const AirdropDetail = () => {
  const [airdrop, setAirdrop] = useState<AirdropType>();
  const [allocation, setAllocation] = useState<AirdropAllocationType[]>([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { id } = useParams();
  const [activeKey, setActiveKey] = useState("info");

  const { address } = useAccount();
  const claimContract = useContract(airdrop?.contract_address, airdropABI);
  const selectedNetworkIds: number | null = useNetworkState();
  const [showNetworkWarning, setShowNetworkWarning] = useState(false);
  const { switchChainAsync } = useSwitchChain();

  const handleWitchChain = async () => {
    const airdopChain = airdrop?.token.chain.chain_id;
    if (airdopChain) {
      try {
        setLoading(true);
        const response = await switchChainAsync({ chainId: airdopChain });
        if (response)
          message.success({
            content: "Switched Chain Successfully",
            className: "custom-class",
            duration: 2,
          });
      } catch (error: any) {
        if (error.name === "UserRejectedRequestError" || error.code === 4001)
          return;
        else
          message.error({
            content: "Failed to switch chain. Check your wallet connection.",
            className: "custom-class",
            duration: 2,
          });
      } finally {
        setLoading(false);
      }
    } else
      message.error({
        type: "error",
        content: "Switched chain but not updated yet. Please try again.",
        className: "custom-class",
        duration: 2,
      });
  };

  const handleGetAirdropDetail = useCallback(async () => {
    if (id) {
      try {
        setLoading(true);
        const response = await getAirdopDetail(id);
        setAirdrop(response);
      } catch (error) {
        messageApi.open({
          type: "error",
          content: "An error occurred!",
        });
      } finally {
        setLoading(false);
      }
    }
  }, [messageApi, id]);

  const handleGetAirdropAllocation = useCallback(async () => {
    if (airdrop?.id && address) {
      try {
        setLoading(true);
        const response = await getAirdropAllocation(
          Number(airdrop?.id),
          address
        );
        setAllocation(response);
      } catch (error) {
        messageApi.open({
          type: "error",
          content: "An error occurred!",
        });
      } finally {
        setLoading(false);
      }
    } else setAllocation([]);
  }, [messageApi, airdrop, address]);

  const handleUpdateTransaction = async (txn: any, id: number) => {
    try {
      setLoading(true);
      const response = await updateTransaction(id, { txn: txn });
      if (response.status === 201) {
        handleGetAirdropAllocation();
      }
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "An error occurred!",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClaimAirdrop = async (allocation: AirdropAllocationType) => {
    try {
      if (!claimContract) {
        message.error({
          type: "error",
          content: "Please Switch Network",
          className: "custom-class",
          duration: 2,
        });
        return;
      }

      const tx = await claimContract.claimAir(
        allocation.id,
        allocation.token_address,
        allocation.amount_string,
        allocation.start_claim_time,
        allocation?.end_claim_time,
        allocation.signature
      );

      const receipt = await tx.wait();

      if (receipt?.status === 1) {
        handleUpdateTransaction(tx.hash, allocation.id);
        message.success({
          type: "success",
          content: "Claimed Successfully",
          className: "custom-class",
          duration: 2,
        });
      } else {
        message.error({
          type: "error",
          content: "Claim Failed: Transaction reverted",
          className: "custom-class",
          duration: 2,
        });
      }
    } catch (err: any) {
      console.error("Claim error:", err);
      message.error({
        type: "error",
        content: err?.reason || err?.message || "Unknown Error",
        className: "custom-class",
        duration: 2,
      });
    }
  };

  useEffect(() => {
    handleGetAirdropDetail();
  }, [handleGetAirdropDetail]);

  useEffect(() => {
    handleGetAirdropAllocation();
  }, [airdrop, address, handleGetAirdropAllocation]);

  useEffect(() => {
    if (
      selectedNetworkIds &&
      airdrop?.token.chain.chain_id &&
      selectedNetworkIds !== airdrop?.token.chain.chain_id
    ) {
      setShowNetworkWarning(true);
    } else setShowNetworkWarning(false);
  }, [airdrop?.token.chain.chain_id, selectedNetworkIds]);

  useEffect(() => {
    if (!address) setActiveKey("info");
  }, [address]);

  return (
    <>
      {contextHolder}
      {showNetworkWarning && address && (
        <div className="network-warning ">
          <div className="content-warning">
            <img src="/images/red-warning.svg" alt="Warning" />
            <div className="text-warning">
              Please switch to {airdrop?.token?.chain?.name}.
              {loading ? (
                <button className="btn-re dis">
                  Waiting
                  <div className="btn-ab dis"></div>
                </button>
              ) : (
                <button className="btn-re" onClick={() => handleWitchChain()}>
                  Switch Now
                  <div className="btn-ab"></div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="bg-customBlue-100 pt-[87px] font-Montserrat">
        <div className="pt-10 pb-16 px-[5vw] xl:px-[8vw] 2xl:px-[12vw] flex flex-col gap-6">
          <div className="py-2 flex items-center gap-[6px] uppercase text-sm tracking-[0.65px]">
            <span
              className="cursor-point text-text-secondary"
              onClick={() => {}}
            >
              AIRDROP
            </span>
            <span>{">"}</span>
            <span className="cursor-pointer text-customBlue-500 font-semibold ">
              {airdrop?.title}
            </span>
          </div>
          {loading ? (
            <AirdropDetailSkeleton />
          ) : (
            <>
              <div className="w-full flex justify-between">
                <div className="w-fit flex items-center gap-[14px]">
                  <img
                    src={airdrop?.avatar}
                    alt=""
                    className="h-[60px] w-[60px] object-cover object-center"
                  />
                  <div className="text-left">
                    <div className="font-Azonix text-xl xs:text-2xl lg:text-3xl xl:text-4xl text-text-primary uppercase line-clamp-1">
                      {airdrop?.title}
                    </div>
                    <div className="text-text-secondary text-lg sm:text-xl leading-[1] font-semibold">
                      ${airdrop?.token.symbol}
                    </div>
                  </div>
                </div>
                <button className="w-fit sm:min-w-[138px] h-fit flex px-1 sm:px-6 py-2 gap-[10px] items-center hover:bg-customBlue-200 rounded-md">
                  <GrShareOption className="h-5 w-5" />
                  <span className="font-semibold  text-text-secondary text-xl leading-[1]">
                    Share
                  </span>
                </button>
              </div>
              <div className="flex flex-wrap justify-between gap-5 sm:gap-10">
                <div className="w-fit flex items-center gap-[6px]">
                  {airdrop?.website_link && (
                    <a
                      className="h-7 w-7 flex items-center justify-center bg-backgroundWhite rounded-md"
                      href={airdrop?.website_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <RiGlobalLine size={18} />
                    </a>
                  )}
                  {airdrop?.telegram_link && (
                    <a
                      className="h-7 w-7 flex items-center justify-center bg-backgroundWhite rounded-md"
                      href={airdrop?.telegram_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FaTelegramPlane size={18} />
                    </a>
                  )}
                  {airdrop?.x_link && (
                    <a
                      className="h-7 w-7 flex items-center justify-center bg-backgroundWhite rounded-md"
                      href={airdrop?.x_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <RiTwitterXLine size={18} />
                    </a>
                  )}
                  {airdrop?.discord_link && (
                    <a
                      className="h-7 w-7 flex items-center justify-center bg-backgroundWhite rounded-md"
                      href={airdrop?.discord_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FaDiscord size={18} />
                    </a>
                  )}
                </div>
                <div className="w-fit flex items-center gap-2">
                  <span className="text-text-secondary font-medium text-base uppercase">
                    Chain Supported:
                  </span>
                  <img
                    src={airdrop?.token.chain.logo}
                    alt="chain"
                    className="w-5 h-5 object-contain"
                  />
                </div>
              </div>
              <div className="border-2 border-solid border-customBlue-400"></div>
              <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                <div className="w-full lg:w-[55%] xl:w-[62%] h-auto aspect-[calc(624/363)]">
                  <img
                    src={airdrop?.background}
                    alt="background"
                    className="h-full w-full object-cover bg-center"
                  />
                </div>
                <div className="w-full lg:w-[45%] xl:w-[38%] flex flex-col">
                  <div className="flex-1 flex flex-col justify-between w-full bg-backgroundWhite p-5 pb-10 text-text-secondary text-left">
                    <div>
                      <AirdropClaimCountdown
                        airdropEnd={airdrop?.end_time}
                        airdropStart={airdrop?.start_time}
                        claimEnd={airdrop?.end_claim_time}
                        claimStart={airdrop?.start_claim_time}
                      />
                      <div className="mt-4 flex justify-between gap-4 flex-wrap">
                        <div className="text-base">Date</div>
                        <div className="font-semibold">
                          {`${formatTimeCommaDate(
                            airdrop?.start_time ?? ""
                          )} - ${formatTimeCommaDate(airdrop?.end_time ?? "")}`}
                        </div>
                      </div>
                    </div>
                    <a
                      href={airdrop?.airdrop_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <button
                        className="mt-8 main-button !w-full"
                        onClick={() => {}}
                      >
                        join now
                      </button>
                    </a>
                  </div>
                  <div className="w-full h-auto flex justify-end">
                    <img
                      src="/images/line-open.png"
                      alt=""
                      className="max-w-full w-[500px] h-auto"
                    />
                  </div>
                </div>
              </div>
              <style>
                {`
                  .airdrop-detail-tabs .ant-tabs-nav::before {
                    border-bottom: solid 1px #C6D5E8 !important;
                  }
                  @media screen and (max-width: 768px) {
                    .airdrop-detail-tabs .ant-tabs-tab + .ant-tabs-tab {
                      margin-left: 32px !important;
                    }
                  }
                `}
              </style>
              <Tabs
                tabBarStyle={{
                  marginBottom: 24,
                  textTransform: "uppercase",
                  fontSize: 20,
                  lineHeight: 1,
                  fontWeight: 600,
                  color: "#2C2C69",
                }}
                activeKey={activeKey}
                onChange={(activeKey) => {
                  if (!showNetworkWarning) setActiveKey(activeKey);
                }}
                className="airdrop-detail-tabs"
              >
                <Tabs.TabPane
                  tab={
                    <span className="text-base sm:text-xl">Airdrop info</span>
                  }
                  key="info"
                >
                  <AirdropInfo airdrop={airdrop} />
                </Tabs.TabPane>
                {address && (
                  <Tabs.TabPane
                    tab={
                      <span className="text-base sm:text-xl">Your rewards</span>
                    }
                    key="reward"
                  >
                    <AirdropReward
                      allocation={allocation || []}
                      {...{ handleClaimAirdrop, airdrop }}
                    />
                  </Tabs.TabPane>
                )}
              </Tabs>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AirdropDetail;
